import React, { useEffect, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { Button } from "../../../components/ui/button.tsx";
import { Input } from "../../../components/ui/input.tsx";
import { useSelector } from "react-redux";
import { API_BASE_URL } from "../../../config/pwaConfig.ts";

const Brands = () => {
  const { authToken = "", id = "" } = useSelector((state: any) => state.loginData || {});
  const [brands, setBrands] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [newBrand, setNewBrand] = useState("");
  const [editBrandId, setEditBrandId] = useState<string | null>(null);
  const [editBrandName, setEditBrandName] = useState("");

  const fetchBrands = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}account/${id}/brands`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      setBrands(response.data.brands || []);
    } catch (error) {
      setBrands([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) fetchBrands();
    // eslint-disable-next-line
  }, [id]);

  const handleAddBrand = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newBrand.trim()) return;
    try {
      await axios.post(
        `${API_BASE_URL}account/${id}/brand/create`,
        { name: newBrand },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      setNewBrand("");
      fetchBrands();
      toast.success("Brand added successfully");
    } catch (error) {
      toast.error("Failed to add brand");
    }
  };

  const handleEditBrand = (brand: any) => {
    setEditBrandId(brand.id);
    setEditBrandName(brand.name);
  };

  const handleUpdateBrand = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editBrandName.trim() || !editBrandId) return;
    try {
      await axios.put(
        `${API_BASE_URL}account/${id}/brand/update`,
        { id: editBrandId, name: editBrandName },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      setEditBrandId(null);
      setEditBrandName("");
      fetchBrands();
      toast.success("Brand updated successfully");
    } catch (error) {
      toast.error("Failed to update brand");
    }
  };

  const handleDeleteBrand = async (brandId: string) => {
    if (!window.confirm("Delete this brand?")) return;
    try {
      await axios.delete(
        `${API_BASE_URL}account/${id}/brand/${brandId}/delete`,
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      fetchBrands();
      toast.success("Brand deleted");
    } catch (error) {
      toast.error("Failed to delete brand");
    }
  };

  return (
    <div className="space-y-6 p-4">
      <h2 className="text-2xl font-bold mb-4">Brands</h2>
      <form onSubmit={editBrandId ? handleUpdateBrand : handleAddBrand} className="flex gap-2 mb-4">
        <Input
          value={editBrandId ? editBrandName : newBrand}
          onChange={e => editBrandId ? setEditBrandName(e.target.value) : setNewBrand(e.target.value)}
          placeholder="Brand name"
          required
        />
        <Button type="submit" className="btn-primary">
          {editBrandId ? "Update" : "Add"}
        </Button>
        {editBrandId && (
          <Button type="button" variant="outline" onClick={() => { setEditBrandId(null); setEditBrandName(""); }}>
            Cancel
          </Button>
        )}
      </form>
      {loading ? (
        <div>Loading...</div>
      ) : brands.length === 0 ? (
        <div className="text-gray-500">No brands found.</div>
      ) : (
        <table className="w-full border rounded">
          <thead>
            <tr className="bg-gray-100">
              <th className="p-2 text-left">Name</th>
              <th className="p-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {brands.map((brand) => (
              <tr key={brand.id} className="border-t">
                <td className="p-2">{brand.name}</td>
                <td className="p-2 text-right space-x-2">
                  <Button size="sm" variant="outline" onClick={() => handleEditBrand(brand)}>
                    Edit
                  </Button>
                  <Button size="sm" variant="outline" className="text-red-600 border-red-200" onClick={() => handleDeleteBrand(brand.id)}>
                    Delete
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default Brands;
